import { RaceType, PlanPhase, PhaseConfig, PlanGeneratorConfig } from '../../types/athlete';

const PHASE_STRUCTURES: Record<RaceType, { totalWeeks: number; phases: Omit<PhaseConfig, 'weeks'>[] }> = {
  ironman: {
    totalWeeks: 24,
    phases: [
      { name: 'Base 1', focus: 'Aerobic foundation', intensityRatio: { E: 70, T: 25, I: 5 }, volumeMultiplier: 0.70 },
      { name: 'Base 2', focus: 'Build endurance', intensityRatio: { E: 60, T: 30, I: 10 }, volumeMultiplier: 0.85 },
      { name: 'Build 1', focus: 'Race-specific', intensityRatio: { E: 50, T: 35, I: 15 }, volumeMultiplier: 0.95 },
      { name: 'Build 2', focus: 'Peak fitness', intensityRatio: { E: 45, T: 40, I: 15 }, volumeMultiplier: 1.00 },
      { name: 'Peak', focus: 'Maintain, sharpen', intensityRatio: { E: 45, T: 40, I: 15 }, volumeMultiplier: 0.80 },
      { name: 'Taper', focus: 'Freshen up', intensityRatio: { E: 50, T: 40, I: 10 }, volumeMultiplier: 0.40 },
    ],
  },
  '70.3': {
    totalWeeks: 16,
    phases: [
      { name: 'Base', focus: 'Foundation', intensityRatio: { E: 65, T: 28, I: 7 }, volumeMultiplier: 0.75 },
      { name: 'Build', focus: 'Build volume', intensityRatio: { E: 55, T: 35, I: 10 }, volumeMultiplier: 0.90 },
      { name: 'Peak', focus: 'Race-specific', intensityRatio: { E: 50, T: 40, I: 10 }, volumeMultiplier: 1.00 },
      { name: 'Taper', focus: 'Sharpening', intensityRatio: { E: 50, T: 40, I: 10 }, volumeMultiplier: 0.45 },
    ],
  },
  olympic: {
    totalWeeks: 12,
    phases: [
      { name: 'Base', focus: 'Fitness foundation', intensityRatio: { E: 60, T: 30, I: 10 }, volumeMultiplier: 0.80 },
      { name: 'Build', focus: 'Race speed', intensityRatio: { E: 50, T: 35, I: 15 }, volumeMultiplier: 0.95 },
      { name: 'Taper', focus: 'Peak', intensityRatio: { E: 50, T: 35, I: 15 }, volumeMultiplier: 0.50 },
    ],
  },
  sprint: {
    totalWeeks: 8,
    phases: [
      { name: 'Build', focus: 'Speed work', intensityRatio: { E: 55, T: 30, I: 15 }, volumeMultiplier: 0.85 },
      { name: 'Peak', focus: 'Race-specific', intensityRatio: { E: 50, T: 35, I: 15 }, volumeMultiplier: 1.00 },
      { name: 'Taper', focus: 'Freshen', intensityRatio: { E: 55, T: 35, I: 10 }, volumeMultiplier: 0.55 },
    ],
  },
};

const PHASE_DURATION_BY_WEEKS: Record<number, Record<RaceType, PlanPhase[]>> = {
  24: {
    ironman: ['Base 1', 'Base 1', 'Base 1', 'Base 1', 'Base 2', 'Base 2', 'Base 2', 'Base 2', 'Build 1', 'Build 1', 'Build 1', 'Build 1', 'Build 2', 'Build 2', 'Build 2', 'Build 2', 'Peak', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper'],
    '70.3': ['Base', 'Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Peak', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper'],
    olympic: ['Base', 'Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Taper', 'Taper', 'Taper', 'Taper'],
    sprint: ['Build', 'Build', 'Build', 'Build', 'Peak', 'Peak', 'Taper', 'Taper'],
  },
  20: {
    ironman: ['Base 1', 'Base 1', 'Base 1', 'Base 1', 'Base 2', 'Base 2', 'Base 2', 'Build 1', 'Build 1', 'Build 1', 'Build 2', 'Build 2', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
    '70.3': ['Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
    olympic: ['Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
    sprint: ['Build', 'Build', 'Build', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
  },
  16: {
    ironman: ['Base 1', 'Base 1', 'Base 1', 'Base 2', 'Base 2', 'Base 2', 'Build 1', 'Build 1', 'Build 1', 'Build 2', 'Build 2', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper'],
    '70.3': ['Base', 'Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Peak', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper'],
    olympic: ['Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
    sprint: ['Build', 'Build', 'Build', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
  },
  12: {
    ironman: ['Base 1', 'Base 1', 'Base 2', 'Base 2', 'Build 1', 'Build 1', 'Build 2', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper'],
    '70.3': ['Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper'],
    olympic: ['Base', 'Base', 'Base', 'Base', 'Build', 'Build', 'Build', 'Build', 'Taper', 'Taper', 'Taper', 'Taper'],
    sprint: ['Build', 'Build', 'Build', 'Peak', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper', 'Taper'],
  },
  8: {
    ironman: ['Base 1', 'Base 1', 'Base 2', 'Build 1', 'Build 2', 'Peak', 'Taper', 'Taper'],
    '70.3': ['Base', 'Base', 'Build', 'Build', 'Peak', 'Taper', 'Taper', 'Taper'],
    olympic: ['Base', 'Base', 'Build', 'Build', 'Peak', 'Taper', 'Taper', 'Taper'],
    sprint: ['Build', 'Build', 'Build', 'Peak', 'Peak', 'Taper', 'Taper', 'Taper'],
  },
};

const DEFAULT_WEEKS_MAP: Record<number, number> = {
  24: 24, 23: 24, 22: 24, 21: 20,
  20: 20, 19: 20, 18: 16, 17: 16,
  16: 16, 15: 16, 14: 12, 13: 12,
  12: 12, 11: 12, 10: 8, 9: 8,
  8: 8, 7: 8, 6: 8, 5: 8,
};

export const getPhaseStructure = (raceType: RaceType, weeksToRace: number): PlanGeneratorConfig => {
  const normalizedWeeks = DEFAULT_WEEKS_MAP[weeksToRace] || 12;
  const structure = PHASE_STRUCTURES[raceType];
  const phaseSchedule = PHASE_DURATION_BY_WEEKS[normalizedWeeks]?.[raceType] || structure.phases.map(p => p.name);

  const phaseCounts: Record<string, number> = {};
  phaseSchedule.forEach(phase => {
    phaseCounts[phase] = (phaseCounts[phase] || 0) + 1;
  });

  const phases: PhaseConfig[] = structure.phases.map(p => ({
    ...p,
    weeks: phaseCounts[p.name] || 4,
  }));

  return {
    totalWeeks: normalizedWeeks,
    phases,
    weeklyVolumeGuidelines: [],
  };
};

export const getPhaseForWeek = (
  weekNumber: number,
  raceType: RaceType,
  weeksToRace: number
): PlanPhase => {
  const normalizedWeeks = DEFAULT_WEEKS_MAP[weeksToRace] || 12;
  const phaseSchedule = PHASE_DURATION_BY_WEEKS[normalizedWeeks]?.[raceType];
  
  if (phaseSchedule && phaseSchedule[weekNumber - 1]) {
    return phaseSchedule[weekNumber - 1];
  }
  
  const structure = PHASE_STRUCTURES[raceType];
  const phaseIndex = Math.min(
    Math.floor((weekNumber - 1) / (normalizedWeeks / structure.phases.length)),
    structure.phases.length - 1
  );
  
  return structure.phases[phaseIndex].name as PlanPhase;
};

export const getPhaseFocus = (phase: PlanPhase): string => {
  const focuses: Record<PlanPhase, string> = {
    'Base 1': 'Aerobic foundation',
    'Base 2': 'Build endurance',
    'Build 1': 'Race-specific fitness',
    'Build 2': 'Peak fitness',
    'Peak': 'Maintain and sharpen',
    'Taper': 'Freshen up for race',
    'Base': 'Fitness foundation',
    'Build': 'Build volume and stamina',
    'Peak (Taper)': 'Race-ready fitness',
    'Build Cycle': 'Progressive build with recovery',
  };
  return focuses[phase] || 'Training';
};

export const getIntensityRatio = (phase: PlanPhase): { E: number; T: number; I: number } => {
  const ratios: Record<PlanPhase, { E: number; T: number; I: number }> = {
    'Base 1': { E: 70, T: 25, I: 5 },
    'Base 2': { E: 60, T: 30, I: 10 },
    'Build 1': { E: 50, T: 35, I: 15 },
    'Build 2': { E: 45, T: 40, I: 15 },
    'Peak': { E: 45, T: 40, I: 15 },
    'Taper': { E: 50, T: 40, I: 10 },
    'Base': { E: 65, T: 28, I: 7 },
    'Build': { E: 55, T: 35, I: 10 },
    'Peak (Taper)': { E: 50, T: 40, I: 10 },
    'Build Cycle': { E: 60, T: 30, I: 10 },
  };
  return ratios[phase] || { E: 60, T: 30, I: 10 };
};

export const getVolumeMultiplier = (phase: PlanPhase): number => {
  const multipliers: Record<PlanPhase, number> = {
    'Base 1': 0.70,
    'Base 2': 0.85,
    'Build 1': 0.95,
    'Build 2': 1.00,
    'Peak': 0.80,
    'Taper': 0.40,
    'Base': 0.75,
    'Build': 0.90,
    'Peak (Taper)': 0.45,
    'Build Cycle': 1.0,
  };
  return multipliers[phase] || 0.80;
};
